import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ImageUrl, ProjectId, ScreenshotId, UserId} from '../../types/ids';
import {AuthService} from '../../services/auth.service';
import {HttpClient, HttpParams} from '@angular/common/http';
import {DateTime, SmallId} from '@webmate-web-core/shared/types/base-types';
import {FullRectangle, Rectangle} from "@webmate-web-core/shared/types/browsersession";

export interface RelativePosition {
    x: number;
    y: number;
}


export interface ArrowOverlayData {
    type: 'ArrowOverlayData';
    start: RelativePosition;
    end: RelativePosition;
    color?: string;
}

export interface RectangleOverlayData {
    type: 'RectangleOverlayData';
    rectangle: Rectangle;
    color?: string;
    width?: number;
}

export interface TextOverlayData {
    type: 'TextOverlayData';
    text: string;
    position: RelativePosition;
    textColor?: string;
    fontSize?: number;
    fontFamily?: string;
    fontWeight?: string;
}

export type OverlayData = TextOverlayData | ArrowOverlayData | RectangleOverlayData;

export interface ImageOverlay {
    overlayData: OverlayData;
    id: SmallId;
    hidden?: boolean;
    name?: string;
    order?: number;
}

export interface ImageMetadata {
    width: number;
    height: number;
    name?: string;
    created?: DateTime,
    createdBy?: UserId,
    position?: FullRectangle;
    overlays?: ImageOverlay[];
}

export type ImageVariantName = 'original' | 'small' | 'medium' | 'large'

/** REST Access to webmate Image API. */
@Injectable({
    providedIn: 'root'
})
export class ImageApi {

    constructor(public authService: AuthService, private http: HttpClient) {
    }

    getImageUrl(imageId: ScreenshotId, variantName?: string, asAttachment?: boolean, attachmentName?: string): ImageUrl {
        const attachmentSuffix = asAttachment ? `?asAttachment=true&attachmentName=${attachmentName || 'screenshot.png'}` : '';
        return this.authService.makeAuthorizedUrl((variantName ? '/images/' + imageId + '/' + variantName : '/images/' + imageId) + attachmentSuffix);
    }

    getImageMetadata(imageId: ScreenshotId, variantName?: string): Observable<ImageMetadata | null> {
        return this.http.get<ImageMetadata>('/images/' + imageId + '/meta');
    }

    storeImage(img: File, projectId: ProjectId): Observable<ScreenshotId> {
        const params = new HttpParams().set('name', img.name);
        return this.http.post<ScreenshotId>(`/projects/${projectId}/images`, img, { params });
    }

    setImageName(id: ScreenshotId, name: string): Observable<any> {
        return this.http.put(`/images/${id}/name`, name);
    }

    setImageOverlays(id: ScreenshotId, overlays: ImageOverlay[]): Observable<any> {
        return this.http.put(`/images/${id}/overlays`, overlays);
    }

}
